import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Landmark, Loader2, ArrowRight, Layers, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChangeBankModal = ({ isOpen, onClose, transaction, bankAccounts = [], onSaved }) => {
  const [selectedBankId, setSelectedBankId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [applyToBatch, setApplyToBatch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      setSelectedBankId(transaction.bank_account_id || "");
      setDueDate(transaction.due_date || "");
      setApplyToBatch(false);
    }
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const currentBank = bankAccounts.find((b) => b.id === transaction.bank_account_id);
  const hasBatch = !!transaction.batch_id;

  const formatCurrency = (val) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val || 0);

  const handleSave = async () => {
    if (!selectedBankId) {
      toast.error("Selecione uma conta bancária.");
      return;
    }
    const bankChanged = selectedBankId !== transaction.bank_account_id;
    const dateChanged = (dueDate || "") !== (transaction.due_date || "");
    if (!bankChanged && !dateChanged && !applyToBatch) {
      toast.info("Nenhuma alteração — informe um novo banco ou uma nova data.");
      return;
    }
    setSaving(true);
    try {
      const payload = { apply_to_batch: applyToBatch };
      if (bankChanged || applyToBatch) payload.bank_account_id = selectedBankId;
      if (dateChanged) payload.due_date = dueDate;
      const res = await axios.patch(`${API}/finance/${transaction.id}/edit-pending`, payload);
      const count = res.data?.modified_count || 1;
      toast.success(
        applyToBatch
          ? `Alteração aplicada em ${count} parcela(s) pendente(s) do lote.${dateChanged ? " (Data alterada só nesta parcela.)" : ""}`
          : "Parcela pendente atualizada."
      );
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar a parcela.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="change-bank-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#D4AF37]/20 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs text-[#D4AF37] uppercase tracking-widest font-bold">
              <Landmark className="w-3.5 h-3.5" />
              <span>Tesouraria • Editar Parcela Pendente</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              Redirecionar banco / Reagendar vencimento
            </h2>
            <p className="text-[11px] text-slate-400 mt-1 italic">
              Enquanto pendente, a data de vencimento é uma expectativa reagendável. Categoria,
              valor e centro de custo (plano de contas) são imutáveis — para corrigi-los, use
              estorno + relançamento.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="change-bank-close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Info do lançamento */}
          <div className="rounded-xl bg-[#131C2E] border border-slate-800 p-4 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Lançamento pendente
            </div>
            <div className="text-sm text-white font-semibold truncate">{transaction.description}</div>
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <span>{transaction.category || "-"}</span>
              <span
                className={`font-mono font-bold ${
                  transaction.type === "receita" ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {transaction.type === "receita" ? "+" : "-"} {formatCurrency(transaction.amount)}
              </span>
            </div>
            {transaction.due_date && (
              <div className="text-[10px] text-slate-500">
                Vencimento: {new Date(transaction.due_date + "T00:00").toLocaleDateString("pt-BR")}
              </div>
            )}
          </div>

          {/* De → Para banco */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block">
              Nova conta bancária
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg border border-slate-700 bg-[#131C2E] px-3 py-2 text-xs text-slate-300">
                <span className="text-[10px] uppercase text-slate-500 block">De</span>
                {currentBank ? (
                  <span className="font-semibold text-white truncate block">{currentBank.name}</span>
                ) : (
                  <span className="italic text-slate-500">Sem banco definido</span>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <span className="text-[10px] uppercase text-slate-500 block mb-1">Para</span>
                <select
                  data-testid="change-bank-select"
                  value={selectedBankId}
                  onChange={(e) => setSelectedBankId(e.target.value)}
                  className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
                >
                  <option value="">Selecione…</option>
                  {bankAccounts.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Nova data de vencimento */}
          <div className="space-y-2">
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              Data de vencimento (reagendar)
            </label>
            <input
              data-testid="change-bank-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-[#D4AF37] outline-none"
            />
            <p className="text-[10px] text-slate-500 italic">
              A data efetiva de pagamento (baixa) só será gravada quando você acionar PAGAR.
              Se aplicar ao lote, a data muda apenas nesta parcela — cada parcela tem seu
              próprio vencimento.
            </p>
          </div>

          {/* Apply to batch */}
          {hasBatch && (
            <label
              data-testid="change-bank-apply-batch"
              className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 cursor-pointer hover:bg-amber-500/10"
            >
              <input
                type="checkbox"
                checked={applyToBatch}
                onChange={(e) => setApplyToBatch(e.target.checked)}
                className="mt-0.5 accent-[#D4AF37]"
              />
              <div>
                <div className="text-xs font-semibold text-amber-200 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" />
                  Aplicar novo banco a todas as parcelas pendentes deste lote
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Redireciona todas as parcelas ainda não pagas (ex: 12 parcelas do IPTU) para o
                  novo banco de uma só vez. <span className="text-amber-300 font-semibold">A data
                  de vencimento é sempre por parcela</span> — não é replicada.
                </div>
              </div>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/80 bg-[#080D18] flex items-center justify-between">
          <button
            onClick={onClose}
            data-testid="change-bank-cancel"
            className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold"
          >
            Cancelar
          </button>
          <button
            data-testid="change-bank-save"
            onClick={handleSave}
            disabled={saving || !selectedBankId}
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Salvando…</span>
              </>
            ) : (
              <>
                <Landmark className="w-3.5 h-3.5" />
                <span>Salvar Alteração</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
